import { Alert, Input, message, Skeleton, Space } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import FormItem, { FormItemProps } from 'antd/lib/form/FormItem';
import * as React from 'react';
import ExternalInstitutionApiService from '../../../api/ExternalInstitutionApiService';
import FeatureFlag from '../../../consts/FeatureFlag';
import * as GetExternalInstitutionDetailsHandler from '../../../handlerModels/GetExternalInstitutionDetailsHandler';
import * as SaveExternalInstitutionDetailsHandler from '../../../handlerModels/SaveExternalInstitutionDetailsHandler';
import ExternalInstitutionDTO from '../../../models/ExternalInstitutionDTO';
import BaseFormProps from '../../../redux/bases/BaseFormProps';
import BaseFormState from '../../../redux/bases/BaseFormState';
import LookupsUtil from '../../../utils/LookupsUtil';
import ValidationRuleUtil from '../../../utils/ValidationRuleUtil';
import ValidationUtil from '../../../utils/ValidationUtil';
import AuthorizedContent from '../../AuthorizedContent';
import ResetButton from '../../buttons/ResetButton';
import SaveButton from '../../buttons/SaveButton';

interface ExternalInstitutionDetailsFormState extends BaseFormState {
  externalInstitutionId: number;
  externalInstitution: ExternalInstitutionDTO | null;
}

interface ExternalInstitutionDetailsFormProps extends BaseFormProps {
  externalInstitutionId: number;
  onSave?: (externalInstitutionId: number) => void;
}

class ExternalInstitutionDetailsForm extends React.Component<ExternalInstitutionDetailsFormProps, ExternalInstitutionDetailsFormState> {
  private readonly _formRef = React.createRef<FormInstance>();
  private readonly _formItems = new Map<string, FormItemProps>()
    .set(ExternalInstitutionDTO.name, {
      required: true,
      name: ExternalInstitutionDTO.name,
      label: 'Name',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(50)
      ]
    })
    .set(ExternalInstitutionDTO.abbreviation, {
      required: true,
      name: ExternalInstitutionDTO.abbreviation,
      label: 'Abbreviation',
      rules: [
        ValidationRuleUtil.required(),
        ValidationRuleUtil.maxLength(10)
      ],
    });

  constructor(props: ExternalInstitutionDetailsFormProps) {
    super(props);

    this.state = {
      externalInstitutionId: 0,
      externalInstitution: ExternalInstitutionDTO.create()
    };
  }

  componentDidMount() {
    this.setState({ loading: true });
    this.loadExternalInstitution();
  }

  componentDidUpdate(prevProps: ExternalInstitutionDetailsFormProps) {
    if (this.props.externalInstitutionId && this.props.externalInstitutionId != prevProps.externalInstitutionId) {
      this.setState({ externalInstitutionId: this.props.externalInstitutionId });
      this.loadExternalInstitution();
    }
  }

  public resetForm = () => {
    this._formRef.current?.resetFields();
    this.setState({ altered: false });
  }

  private loadExternalInstitution() {
    if (this.props.externalInstitutionId == 0) {
      this.loadNew();
    }
    else {
      this.loadExisting();
    }
  }

  private loadExisting = () => {
    if (this.props.externalInstitutionId) {
      ExternalInstitutionApiService.getExternalInstitutionDetails(this.props.externalInstitutionId)
        .then((results: GetExternalInstitutionDetailsHandler.Result) => {
          if (results && results.externalInstitution) {
            this.setState({ externalInstitutionId: results.externalInstitution.id, externalInstitution: results.externalInstitution, loading: false });
          }

          this.resetForm();
        }).catch(() => {
          this.setState({ error: true });
        }).finally(() => {
          this.setState({ loading: false });
        });
    }
  }

  private loadNew = () => {
    this.setState({ externalInstitution: ExternalInstitutionDTO.create(), loading: false });
  }

  private handleChange = () => {
    this.setState({ altered: true });
  }

  private handleSubmit = () => {
    this.setState({ submitting: true });

    const request = SaveExternalInstitutionDetailsHandler.Request.create({
      externalInstitutionId: this.state.externalInstitutionId,
      externalInstitution: ExternalInstitutionDTO.create(this._formRef ? (this._formRef.current as any).getFieldsValue() : null)
    });

    ExternalInstitutionApiService.saveExternalInstitutionDetails(request)
      .then((result: SaveExternalInstitutionDetailsHandler.Result) => {
        this.setState({
          submitted: true,
          externalInstitution: result?.externalInstitution,
        });

        if (result?.succeeded) {
          message.success('Saved');

          if (this.props.onSave && result.externalInstitution?.id) {
            this.props.onSave(result.externalInstitution.id);
            this.resetForm();
          }

          LookupsUtil.invalidateCache(ExternalInstitutionDTO.className);
        }
        else {
          this.setState({
            error: !result?.succeeded,
            message: result?.errors.join('\n'),
            fieldErrors: result?.fieldErrors
          });
          message.error('External Institution could not be saved. ' + this.state.message);
        }
      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('External Institution could not be saved.');
      })
      .finally(() => {
        this.setState({ loading: false, submitting: false });
      });
  }

  render() {
    if (this.state.loading) {
      return <Skeleton active={true} />;
    }

    return (
      <Space size="small" direction="vertical">
        {this.renderErrors()}
        <Form
          ref={this._formRef}
          layout="vertical"
          onValuesChange={this.handleChange}
          onFinish={this.handleSubmit} requiredMark={true}>
          <FormItem
            {...this._formItems.get(ExternalInstitutionDTO.abbreviation)}
            initialValue={this.state.externalInstitution?.abbreviation}
            {...ValidationUtil.getValidation(ExternalInstitutionDTO.abbreviation, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>
          <FormItem
            {...this._formItems.get(ExternalInstitutionDTO.name)}
            initialValue={this.state.externalInstitution?.name}
            {...ValidationUtil.getValidation(ExternalInstitutionDTO.name, this.state.fieldErrors, this.state.submitted)}>
            <Input disabled={!this.props.isEditing || this.state.loading || this.state.submitting} />
          </FormItem>

          {this.renderSave()}
        </Form>
      </Space>
    );
  }

  renderSave() {
    if (this.props.isEditing) {
      return (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_EXTERNAL_INSTITUTIONS]}>
          <Space direction={'horizontal'} >
            <SaveButton saving={this.state.saving} />
            <ResetButton onConfirm={this.resetForm} disabled={!this.state.altered} />
          </Space>
        </AuthorizedContent>
      );
    }
  }

  renderErrors() {
    if (this.state.error && this.state.submitted) {
      return <Alert type="error" message='Error' showIcon={true} description='There were errors submitting your request. Please review the fields below.' />;
    }
  }
}

export default ExternalInstitutionDetailsForm;
